'use strict';
import {makeActionCreator} from '../actions';


/**
 * @param {ReduxObservableItemOptions} options
 * @return {ReduxObservableItemActions}
 */
export function buildActions(options) {
  const actions = {};

  // Item actions
  const INIT_ITEM = `${options.prefix}/INIT_ITEM`;
  const HYDRATE_ITEM_STATE = `${options.prefix}/HYDRATE_ITEM_STATE`;
  const LOAD_ITEM_SUCCESS = `${options.prefix}/LOAD_ITEM_SUCCESS`;
  const LOAD_ITEM_ERROR = `${options.prefix}/LOAD_ITEM_ERROR`;
  const SYNC_ITEM_SUCCESS = `${options.prefix}/SYNC_ITEM_SUCCESS`;
  const DESTROY_ITEM = `${options.prefix}/DESTROY_ITEM`;

  // Item-affecting CRUD actions
  const DELETE_ITEM = `${options.prefix}/DELETE_ITEM`;
  const DELETE_ITEM_SUCCESS = `${options.prefix}/DELETE_ITEM_SUCCESS`;
  const DELETE_ITEM_ERROR = `${options.prefix}/DELETE_ITEM_ERROR`;

  /**
   * Store the parent ID the action is targeting.
   * @typedef {Object} ReduxObservableActionMeta
   * @property {String} [parentId]
   */

  /**
   * The initItem action may optionally load a locally cached item.
   * This will typically occur when transitioning from a list view to a detail view.
   * @typedef {Object} InitItemPayload
   * @property {String} id
   * @property {T} [existingItem] If the instance is already available on the client, the local copy can initialize the item.
   */

  /**
   * @param {InitItemPayload} payload
   * @return {ReduxAction<InitItemPayload>}
   */
  actions.initItem = makeActionCreator(INIT_ITEM);

  /**
   * @param {ReduxObservableItemState} payload
   * @return {ReduxAction<ReduxObservableItemState>}
   */
  actions.hydrateState = makeActionCreator(HYDRATE_ITEM_STATE);

  /**
   * Contains a map of updated item elements with their DB ID as the keys.
   * @typedef {Object} LoadItemSuccessPayload
   * @property {T} item
   */

  /**
   *
   * @param {LoadItemSuccessPayload} payload
   * @param {ReduxObservableActionMeta} meta
   * @return {ReduxAction<LoadItemSuccessPayload>}
   */
  actions.loadItemSuccess = makeActionCreator(LOAD_ITEM_SUCCESS);

  /**
   * @typedef {Object} LoadItemError
   * @property {Error} err
   * @property {ViewMode} mode
   */

  /**
   * @param {LoadItemError} payload
   * @return {ReduxAction<LoadItemError>}
   */
  actions.loadItemError = makeActionCreator(LOAD_ITEM_ERROR);

  /**
   * @typedef {Object} SyncItemSuccessPayload
   * @property {T} item
   */

  /**
   * @param {SyncItemSuccessPayload} payload
   * @return {ReduxAction<SyncItemSuccessPayload>}
   */
  actions.syncItemSuccess = makeActionCreator(SYNC_ITEM_SUCCESS);

  /**
   * @return {ReduxAction<Object>}
   */
  actions.destroyItem = makeActionCreator(DESTROY_ITEM);

  /**
   * @param {Void} payload
   * @return {ReduxAction<Void>}
   */
  actions.deleteItem = makeActionCreator(DELETE_ITEM);

  /**
   * @param {Void} payload
   * @return {ReduxAction<Void>}
   */
  actions.deleteItemSuccess = makeActionCreator(DELETE_ITEM_SUCCESS);

  /**
   * @typedef {Object} DeleteItemErrorPayload
   * @property {String} id
   * @property {Error} err
   */

  /**
   * @param {DeleteItemErrorPayload} payload
   * @return {ReduxAction<DeleteItemErrorPayload>}
   */
  actions.deleteItemError = makeActionCreator(DELETE_ITEM_ERROR);

  return actions;
}

/**
 * @typedef {Object} ReduxObservableItemActions
 * @property {Function|ReduxActionCreator<InitItemPayload>} initItem
 * @property {Function|ReduxActionCreator<ReduxObservableItemState>} hydrateState
 * @property {Function|ReduxActionCreator<LoadItemSuccessPayload>} loadItemSuccess
 * @property {Function|ReduxActionCreator<LoadItemError>} loadItemError
 * @property {Function|ReduxActionCreator<SyncItemSuccessPayload>} syncItemSuccess
 * @property {Function|ReduxActionCreator<Void>} destroyItem
 *
 * @property {Function|ReduxActionCreator<Void>} deleteItem
 * @property {Function|ReduxActionCreator<Void>} deleteItemSuccess
 * @property {Function|ReduxActionCreator<DeleteItemErrorPayload>} deleteItemError
 */



// WEBPACK FOOTER //
// ./src/js/_libs/redux-observable-list/src/item/actions.js